import { useEffect, useState } from 'react'
import { Check } from 'lucide-react'
import { startPayfastCheckout } from '@/lib/payfastCheckout'
import {
  BILLING_PLANS,
  formatCount,
  formatZar,
  isPaidPlanId,
  planLimits,
  planRank,
  type PaidPlanId,
} from '@/lib/billingPlans'

interface PlanCardsProps {
  currentTier: string
  // True when the user already has an active paid subscription.
  hasActiveSubscription: boolean
  // Plan chosen on the pricing page, if any. Highlighted, never auto-submitted.
  highlightPlan?: PaidPlanId | null
}

export default function PlanCards({ currentTier, hasActiveSubscription, highlightPlan }: PlanCardsProps) {
  const [pending, setPending] = useState<PaidPlanId | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!highlightPlan) return
    document.getElementById(`plan-card-${highlightPlan}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlightPlan])

  const handleUpgrade = async (plan: PaidPlanId) => {
    setError(null)
    // A second subscription would not replace the first, so the customer
    // would be billed for both. Ask them to cancel first.
    if (hasActiveSubscription) {
      setError('To switch plan, cancel your current subscription first (see Billing details below), then choose the new plan. This avoids being billed twice.')
      return
    }
    setPending(plan)
    try {
      await startPayfastCheckout('subscribe', { plan })
      // The browser is now leaving for PayFast.
    } catch (err: any) {
      setError(err.message || 'Something went wrong starting checkout. Please try again.')
      setPending(null)
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 mb-1">Your plan</h2>
      <p className="text-sm text-gray-800 mb-4">
        You are on the <span className="font-semibold capitalize">{currentTier}</span> plan.
      </p>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      <div className="grid gap-3 sm:grid-cols-3">
        {BILLING_PLANS.map(plan => {
          const limits = planLimits(plan.id)
          const isCurrent = plan.id === currentTier
          const isHigher = planRank(plan.id) > planRank(currentTier)
          const isChosen = isHigher && plan.id === highlightPlan
          return (
            <div
              key={plan.id}
              id={`plan-card-${plan.id}`}
              className={
                'rounded-xl border p-4 flex flex-col ' +
                (isChosen
                  ? 'border-gold-500 bg-gold-50 ring-2 ring-gold-500 shadow-lg'
                  : plan.id === 'premium' ? 'border-gold-500/60 bg-gold-50' : 'border-gray-200 bg-white')
              }
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-900">{plan.name}</span>
                {plan.badge && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gold-500/20 text-gold-700">
                    {plan.badge}
                  </span>
                )}
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatZar(plan.priceZar)}
                {plan.priceZar > 0 && <span className="text-xs font-normal text-gray-500"> per month</span>}
              </p>
              <ul className="space-y-1.5 text-sm text-gray-700 my-4 flex-1">
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-1 text-gold-600 shrink-0" />
                  {formatCount(limits.mt_chars)} MT characters per month
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-1 text-gold-600 shrink-0" />
                  {formatCount(limits.mt_chars_daily)} MT characters per day
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-1 text-gold-600 shrink-0" />
                  {formatCount(limits.ai_queries)} AI queries per month
                </li>
              </ul>
              {isCurrent ? (
                <span className="w-full text-center py-2 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium">
                  Current plan
                </span>
              ) : isHigher && isPaidPlanId(plan.id) ? (
                <button
                  type="button"
                  onClick={() => handleUpgrade(plan.id as PaidPlanId)}
                  disabled={pending !== null}
                  className="w-full py-2 rounded-lg bg-gold-500 hover:bg-gold-400 text-slate-900 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {pending === plan.id ? 'Redirecting…' : isChosen ? 'Continue to payment' : 'Upgrade'}
                </button>
              ) : null}
            </div>
          )
        })}
      </div>
      <p className="text-xs text-gray-500 mt-3">
        Prices are in South African rand, billed monthly through PayFast. You can cancel at any time below.
      </p>
    </div>
  )
}
